'use client';

import React, { createContext, useContext, useRef, useState } from 'react';
import { getUserSession, getUserToken } from '@/network/networkCallHelper';
import { platformApps } from '@/constants/enums/enums';
import { useRouter } from 'next/navigation';
import { Auth0CustomError } from '@/helper/error';
import { User } from '@auth0/nextjs-auth0/types';

type AuthenticationContextType = {
  getAuthenticatedToken: () => Promise<string>;
  initSession: () => Promise<void>;
  userData: User | null;
  setUserData: React.Dispatch<React.SetStateAction<User | null>>;
  isLoading: boolean;
  setAccountDrawerRef: React.MutableRefObject<((open: boolean) => void) | null>;
};
const AuthenticationContext = createContext({} as AuthenticationContextType);

export const useAuthenticationContext = () => {
  return useContext(AuthenticationContext);
};

export const AuthenticationProvider = ({ children }: { children: React.ReactNode }) => {
  const [userData, setUserData] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const setAccountDrawerRef = useRef<null | ((open: boolean) => void)>(null);
  const userRef = useRef({
    token: '',
    expiry: 0,
    message: '',
  });

  const router = useRouter();

  async function initSession() {
    // if token is already set, then no need to get the token
    if (userRef.current.token) return;

    try {
      // eslint-disable-next-line require-use-server
      const session = await getUserSession();
      // const session = await getUserToken();
      if (!session || session.message) {
        throw new Auth0CustomError(`${session?.message}`, session?.code);
      }
      userRef.current.token = session.token;
      userRef.current.expiry = session.expiry;
      localStorage.setItem('ecToken', session.token);
      if (session?.userData) {
        setUserData(session?.userData);
        setIsLoading(false);
      }
    } catch (error: any) {
      if (
        process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.HUB &&
        (error.code === 'missing_session' || error.code === 'ERR_JWT_EXPIRED')
      ) {
        router.push('/auth/login?returnTo=/');
        return;
      }
      if (error?.message !== 'missing session') {
        console.log('Auth0 error: ', error?.message);
        // customToaster(`${error}`);
      }
    }
  }

  async function getAuthenticatedToken() {
    const localStorageToken = localStorage.getItem('ecToken');
    const timeNow = Math.floor(Date.now() / 1000);
    try {
      // if there is no token or if the token is expired
      if (!localStorageToken || timeNow > userRef.current.expiry) {
        // eslint-disable-next-line require-use-server
        const session = await getUserToken();

        if (!session || session.message) {
          throw new Auth0CustomError(`${session?.message}`, session?.code);
        }
        userRef.current.token = session.token;
        userRef.current.expiry = session.expiry;
        localStorage.setItem('ecToken', session.token);
        return session.token;
      }

      return localStorageToken;
    } catch (error: any) {
      console.log('Auth0 error: ', error?.message);

      if (
        process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.HUB &&
        (error.code === 'missing_session' || error.code === 'ERR_JWT_EXPIRED')
      ) {
        router.push('/auth/login?returnTo=/');
        return '';
      }

      // if not token is found
      // customToaster(`Error : ${error}`);
      return '';
    }
  }

  return (
    <AuthenticationContext.Provider
      value={{
        getAuthenticatedToken,
        initSession,
        userData,
        setUserData,
        isLoading,
        setAccountDrawerRef,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};
